import { supabase } from "@/lib/supabase/client";
import type { Database, TrackingIdentifierType } from "@/types/database";

export type ShipmentTracking = Database["public"]["Tables"]["shipment_tracking"]["Row"];
export type ShipmentTrackingInput = Omit<Database["public"]["Tables"]["shipment_tracking"]["Insert"], "tenant_id" | "shipment_id" | "provider">;

const columns = "id, tenant_id, shipment_id, provider, provider_reference, tracking_identifier, tracking_identifier_type, status, last_synced_at, next_sync_at, last_error, consecutive_failures, is_active, created_at, updated_at";

export async function listTracking(tenantId: string, shipmentId: string) {
  const { data, error } = await supabase.from("shipment_tracking").select(columns).eq("tenant_id", tenantId).eq("shipment_id", shipmentId).order("created_at");
  if (error) throw error;
  return data ?? [];
}

export async function createTracking(tenantId: string, shipmentId: string, input: { trackingIdentifier: string; trackingIdentifierType: TrackingIdentifierType; providerReference?: string | null }) {
  const { data, error } = await supabase.from("shipment_tracking").insert({
    tenant_id: tenantId,
    shipment_id: shipmentId,
    provider: "MOCK",
    tracking_identifier: input.trackingIdentifier.trim(),
    tracking_identifier_type: input.trackingIdentifierType,
    provider_reference: input.providerReference?.trim() || null,
  }).select(columns).single();
  if (error) throw error;
  return data;
}

export async function deleteTracking(tenantId: string, trackingId: string) {
  const { error } = await supabase.from("shipment_tracking").delete().eq("tenant_id", tenantId).eq("id", trackingId);
  if (error) throw error;
}

export async function syncTracking(trackingId: string) {
  const workerUrl = import.meta.env.VITE_TRACKING_WORKER_URL as string | undefined;
  if (!workerUrl) throw new Error("Tracking sync is not configured for this environment.");
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error("Your session has expired. Please sign in again.");
  const response = await fetch(`${workerUrl.replace(/\/$/, "")}/api/tracking/sync`, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
    body: JSON.stringify({ shipmentTrackingId: trackingId }),
  });
  if (!response.ok) throw new Error(response.status === 403 ? "You do not have access to synchronize this tracking record." : "Unable to synchronize tracking right now.");
}
